import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Input, Card, Heading, BodyText, Select, Button, Switch, RadioGroup, TableScroll } from '@aaimplatform/ui';
import { Plus, Trash2, Shield, Info } from 'lucide-react';
import type { components } from '@aaimplatform/institutional-types/generated/collateral-policy';
import { CHA_TIER_LABELS } from './chaTier';
// The whole-number field and the tab-validity tracker live beside the CHA
// action windows, the first editor that needed them (ruling 71).
import { WholeNumberInput, useFieldValidity } from './ChaThresholdsTab';

type MonitoringPolicy = components['schemas']['MonitoringPolicy'];
type EscalationStep = components['schemas']['EscalationStep'];
type AlertChannel = components['schemas']['AlertChannel'];
type RedTierHandoff = components['schemas']['RedTierHandoff'];
type ReportingFrequency = components['schemas']['ReportingFrequency'];

interface MonitoringTabProps {
  policy: MonitoringPolicy;
  onChange: (policy: MonitoringPolicy) => void;
  /**
   * Platform availability of the flag-gated channels (collateral.monitoring.*
   * feature flags). A channel absent from the map is offered. 'unknown' means
   * the platform flags could not be read (r3 fi-g7-3).
   */
  channelAvailability?: Partial<Record<AlertChannel, 'available' | 'unavailable' | 'unknown'>>;
  /**
   * Called with false while any escalation delay holds an entry that is not a
   * whole number of hours (an emptied field included), and with true once every
   * delay is valid again. The page holds Save while it reads false.
   */
  onValidityChange?: (valid: boolean) => void;
}

/** Where webhook endpoints and their HMAC secrets are managed (FI Configuration). */
export const WEBHOOK_ENDPOINTS_HREF = '/admin/config/webhook-endpoints';

const CHANNEL_OPTIONS: { value: AlertChannel; label: string; description: string }[] = [
  { value: 'EMAIL', label: 'Email', description: 'Deliver alerts to staff email addresses' },
  { value: 'WEBHOOK', label: 'Webhook', description: 'Push to your systems via HMAC-signed webhook' },
  { value: 'SMS', label: 'SMS', description: 'Text message alerts for critical tiers' },
  { value: 'PORTAL', label: 'Portal', description: 'In-app notification in the admin portal' },
];

/**
 * RED tier handoff is ONE choice, so it renders as a radio group. It was two
 * independent switches whose handler always set its own option, so switching
 * the active one off did nothing (UI run 2026-09-27, fi-g7-11).
 */
const HANDOFF_OPTIONS: { value: RedTierHandoff; label: string; description: string }[] = [
  {
    value: 'FI_REVIEW_QUEUE',
    label: 'Review Queue',
    description:
      'Alert placed in the admin portal queue. Staff reviews and decides next steps at their discretion.',
  },
  {
    value: 'FI_CONTACT_MEMBER',
    label: 'Staff Contacts Member',
    description:
      'Your staff contacts the member directly to discuss the collateral situation and work toward resolution.',
  },
];

const FREQUENCY_OPTIONS = [
  { value: 'REAL_TIME', label: 'Real-time' },
  { value: 'DAILY_DIGEST', label: 'Daily digest' },
  { value: 'WEEKLY_SUMMARY', label: 'Weekly summary' },
];

const TIER_OPTIONS = (['YELLOW', 'ORANGE', 'RED'] as const).map((value) => ({
  value,
  label: CHA_TIER_LABELS[value],
}));

const ROLE_OPTIONS = [
  { value: 'LOAN_OFFICER', label: 'Loan Officer' },
  { value: 'BRANCH_MANAGER', label: 'Branch Manager' },
  { value: 'CHIEF_LENDING_OFFICER', label: 'Chief Lending Officer' },
  { value: 'COMPLIANCE_OFFICER', label: 'Compliance Officer' },
  { value: 'CEO', label: 'CEO' },
];

/**
 * MonitoringTab - Alert channels, escalation sequences, and RED tier handoff
 *
 * This is where the FI defines who gets notified at each CHA tier,
 * through which channels, and what happens when a pledge reaches RED.
 * RED is the terminal tier -- Aaim hands off to the FI for human decision.
 */
export function MonitoringTab({ policy, onChange, channelAvailability, onValidityChange }: MonitoringTabProps) {
  const [newStepTier, setNewStepTier] = useState<string>('YELLOW');
  const reportValidity = useFieldValidity(onValidityChange);
  const [newStepRole, setNewStepRole] = useState<string>('LOAN_OFFICER');

  // Defensive defaults for properties that may be absent on first load
  const alertChannels: AlertChannel[] = policy.alertChannels ?? [];
  const escalationSequence: EscalationStep[] = policy.escalationSequence ?? [];

  const handleChannelToggle = (channel: AlertChannel) => {
    const current = alertChannels;
    const updated = current.includes(channel)
      ? current.filter(c => c !== channel)
      : [...current, channel];
    onChange({ ...policy, alertChannels: updated });
  };

  const handleStepChange = (index: number, field: keyof EscalationStep, value: unknown) => {
    const updated = escalationSequence.map((step, i) =>
      i === index ? { ...step, [field]: value } : step,
    );
    onChange({ ...policy, escalationSequence: updated });
  };

  const handleAddStep = () => {
    const step: EscalationStep = {
      tier: newStepTier as EscalationStep['tier'],
      recipientRole: newStepRole,
      notifyAfterHours: 0,
    };
    onChange({
      ...policy,
      escalationSequence: [...escalationSequence, step],
    });
  };

  const handleRemoveStep = (index: number) => {
    const updated = escalationSequence.filter((_, i) => i !== index);
    onChange({ ...policy, escalationSequence: updated });
  };

  return (
    <div className="space-y-6">
      {/* Page title lives in the sticky topnav (AppHeader); no redundant H1.
          Reset and Save sit in one row on CollateralPolicyPage (r3 fi-g7-11). */}

      {/* No service reads alert channels, escalation steps, the RED handoff
          choice or the reporting frequency yet, so the page says the settings
          are recorded rather than describing delivery (r3 fi-g7-3). */}
      <div
        role="note"
        className="bg-elevated border border-recessed rounded-sm p-4 flex items-start gap-3"
      >
        <Info className="w-5 h-5 text-tertiary flex-shrink-0 mt-0.5" aria-hidden="true" />
        <BodyText variant="ui" size="sm" color="secondary">
          These monitoring settings are saved with your collateral policy. Alert
          delivery does not apply them yet: channels, escalation steps, the RED
          handoff and the reporting frequency on this page are recorded, not acted on.
        </BodyText>
      </div>

      {/* Alert Channels */}
      <Card>
        <div className="px-5 py-4">
          <Heading level={4} size="card" className="mb-2">Alert Channels</Heading>
          <BodyText variant="ui" size="sm" color="secondary" className="mb-5">
            The channels your institution wants collateral health alerts sent through.
          </BodyText>

          <div className="space-y-3">
            {CHANNEL_OPTIONS.map(channel => {
              const isActive = alertChannels.includes(channel.value);
              // A channel absent from the map is not flag-gated. A channel the
              // platform does not offer cannot be turned on; one already on can
              // still be turned off.
              const availability = channelAvailability?.[channel.value];
              const canToggle = availability === undefined || availability === 'available' || isActive;
              const description =
                availability === 'unavailable'
                  ? 'Not available on the platform'
                  : availability === 'unknown'
                    ? 'Platform availability could not be read'
                    : channel.description;
              return (
                <div
                  key={channel.value}
                  className={`flex items-center justify-between p-4 rounded-sm border transition-colors ${
                    isActive ? 'border-growth/30 bg-growth/5' : 'border-recessed'
                  }`}
                >
                  <div className="flex-1">
                    <BodyText variant="ui" size="sm" className="font-medium">{channel.label}</BodyText>
                    <BodyText variant="ui" size="xs" color="tertiary">{description}</BodyText>
                    {/* Webhook endpoints and their HMAC secrets are managed on
                        one page. This tab carried a second webhook form that
                        stored the secret in the collateral policy, where every
                        policy read returned it (r3 fi-g7-1). */}
                    {channel.value === 'WEBHOOK' && (
                      <BodyText variant="ui" size="xs" color="tertiary" className="mt-1">
                        Endpoints and signing secrets are managed under{' '}
                        <Link to={WEBHOOK_ENDPOINTS_HREF} className="text-accent hover:underline">
                          Webhook Endpoints
                        </Link>
                        .
                      </BodyText>
                    )}
                  </div>
                  {/* The per-channel gear button had no destination and was
                      removed (fi-g7-9): email recipients are set in the
                      escalation rows below. */}
                  <Switch
                    aria-label={`${channel.label} alerts`}
                    isSelected={isActive}
                    isDisabled={!canToggle}
                    onChange={() => handleChannelToggle(channel.value)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Escalation Sequence */}
      <Card>
        <div className="px-5 py-4">
          <Heading level={4} size="card" className="mb-2">Escalation Sequence</Heading>
          <BodyText variant="ui" size="sm" color="secondary" className="mb-5">
            Who your institution wants notified at each CHA tier, and after how many hours.
          </BodyText>

          {/* Steps table -- horizontal scroll on narrow viewports so the
              12-col emulated table keeps its column alignment instead of
              collapsing into unreadable slivers. */}
          <TableScroll label="Escalation sequence" className="space-y-2 mb-5">
            <div className="grid grid-cols-12 gap-3 px-3 py-2 text-label-xs font-semibold uppercase text-tertiary min-w-table-dense">
              <div className="col-span-2">Tier</div>
              <div className="col-span-4">Recipient Role</div>
              <div className="col-span-2">Email</div>
              <div className="col-span-3">Delay (hours)</div>
              <div className="col-span-1" />
            </div>

            {escalationSequence.map((step, index) => (
              <div
                key={`${step.tier}-${step.recipientRole}-${index}`}
                className="grid grid-cols-12 gap-3 items-center px-3 py-2 bg-elevated rounded-sm min-w-table-dense"
              >
                {/* The kit Select takes no aria-label, so each one is named by
                    a visually hidden <label> bound to its id (fi-g7-10). */}
                <div className="col-span-2">
                  <label htmlFor={`escalation-${index}-tier`} className="sr-only">
                    Step {index + 1} tier
                  </label>
                  <Select
                    id={`escalation-${index}-tier`}
                    value={step.tier}
                    onChange={(e) => handleStepChange(index, 'tier', e.target.value)}
                    options={TIER_OPTIONS}
                    className="text-sm"
                  />
                </div>
                <div className="col-span-4">
                  <label htmlFor={`escalation-${index}-role`} className="sr-only">
                    Step {index + 1} recipient role
                  </label>
                  <Select
                    id={`escalation-${index}-role`}
                    value={step.recipientRole}
                    onChange={(e) => handleStepChange(index, 'recipientRole', e.target.value)}
                    options={ROLE_OPTIONS}
                    className="text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    aria-label={`Step ${index + 1} recipient email`}
                    value={step.recipientEmail || ''}
                    onChange={(e) => handleStepChange(index, 'recipientEmail', e.target.value)}
                    placeholder="Optional"
                    className="text-sm"
                  />
                </div>
                <div className="col-span-3">
                  {/* An emptied delay is invalid with an inline error; it never
                      stores Number('') = 0 (ruling 71). */}
                  <WholeNumberInput
                    id={`escalation-${index}-delay`}
                    aria-label={`Step ${index + 1} delay (hours)`}
                    value={step.notifyAfterHours}
                    onChange={(hours) => handleStepChange(index, 'notifyAfterHours', hours)}
                    onValidityChange={(valid) => reportValidity(`escalation-${index}-delay`, valid)}
                    min={0}
                    unit="hours"
                    className="text-sm font-mono"
                  />
                </div>
                <div className="col-span-1 text-right">
                  <button
                    type="button"
                    onClick={() => handleRemoveStep(index)}
                    className="text-tertiary hover:text-error transition-colors p-1"
                    aria-label="Remove step"
                    title="Remove step"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </TableScroll>

          {/* Width lives on a wrapper: the kit Select's own wrapper is full
              width, so a width class passed to it sized only the <select> and
              left each chevron at the far edge of the card (fi-g7-8). */}
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
            <div className="w-full sm:w-40">
              <label htmlFor="new-escalation-tier" className="sr-only">
                New step tier
              </label>
              <Select
                id="new-escalation-tier"
                value={newStepTier}
                onChange={(e) => setNewStepTier(e.target.value)}
                options={TIER_OPTIONS}
                className="text-sm"
              />
            </div>
            <div className="w-full sm:w-56">
              <label htmlFor="new-escalation-role" className="sr-only">
                New step recipient role
              </label>
              <Select
                id="new-escalation-role"
                value={newStepRole}
                onChange={(e) => setNewStepRole(e.target.value)}
                options={ROLE_OPTIONS}
                className="text-sm"
              />
            </div>
            <Button onClick={handleAddStep} variant="secondary" className="flex items-center gap-1">
              <Plus className="w-4 h-4" />
              Add Step
            </Button>
          </div>
        </div>
      </Card>

      {/* RED Tier Handoff */}
      <Card>
        <div className="px-5 py-4">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-5 h-5 text-error" />
            <Heading level={4} size="card">RED Tier Handoff</Heading>
          </div>
          <BodyText variant="ui" size="sm" color="secondary" className="mb-5">
            RED is the terminal tier. Aaim does not auto-liquidate. When a pledge
            reaches RED, the system hands off to your institution for a human decision.
            Choose how that handoff works.
          </BodyText>

          <RadioGroup
            aria-label="RED tier handoff"
            variant="card"
            value={policy.redTierHandoff}
            onChange={(value) => onChange({ ...policy, redTierHandoff: value as RedTierHandoff })}
            options={HANDOFF_OPTIONS}
          />
        </div>
      </Card>

      {/* Reporting Frequency */}
      <Card>
        <div className="px-5 py-4">
          <Heading level={4} size="card" className="mb-2">Reporting Frequency</Heading>
          <BodyText variant="ui" size="sm" color="secondary" className="mb-5">
            How often to receive aggregate collateral health reports
            (independent of individual alert notifications).
          </BodyText>

          <div className="w-full sm:w-56">
            <label htmlFor="reporting-frequency" className="sr-only">
              Reporting frequency
            </label>
            <Select
              id="reporting-frequency"
              value={policy.reportingFrequency}
              onChange={(e) => onChange({ ...policy, reportingFrequency: e.target.value as ReportingFrequency })}
              options={FREQUENCY_OPTIONS}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}
